/**
 * JWT Tenant Resolver
 *
 * Extracts the tenant ID from an MSAL-acquired access token at runtime.
 * This replaces the build-time VITE_CUSTOMER_ID approach for SaaS deployments
 * where a single shared frontend (clerk.goviq.us) serves multiple tenants.
 *
 * Configuration:
 *   VITE_TENANT_CLAIM_NAME — the Entra ID extension attribute claim name emitted
 *   in the access token after E1 portal configuration.
 *   Typical form: extension_{appIdNoHyphens}_tenantId
 *   Example: extension_abc123def456_tenantId
 *
 * Dev fallback:
 *   If VITE_TENANT_CLAIM_NAME is not yet configured OR the claim is absent in the
 *   token (pre-E1 local development), falls back to VITE_CUSTOMER_ID when running
 *   in Vite dev server (import.meta.env.DEV). This lets developers work locally
 *   before the Entra extension attribute is set up.
 *
 * Production behaviour:
 *   If the claim is absent and not in dev mode, throws with a clear message
 *   pointing to the E1 setup runbook. This surfaces misconfigured user provisioning
 *   loudly rather than silently failing.
 *
 * See DEVELOPER_GUIDE.md — "Tenant Claim Configuration (E1)" for setup instructions.
 */

/**
 * Decode a JWT payload section (base64url → JSON object).
 * Does NOT verify the signature — Azure AD handles that upstream.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Extract the tenant ID from a JWT access token using the configured claim name.
 * Returns null if VITE_TENANT_CLAIM_NAME is not set or the claim is absent.
 * Use getTenantIdOrThrow() when a missing tenant ID should be a hard error.
 */
export function resolveTenantIdFromToken(token: string): string | null {
  const claimName = import.meta.env.VITE_TENANT_CLAIM_NAME as string | undefined;
  if (!claimName) return null;
  const payload = decodeJwtPayload(token);
  const value = payload?.[claimName];
  return typeof value === 'string' && value ? value : null;
}

/**
 * Resolve the tenant ID with dev fallback. Throws if resolution fails entirely.
 *
 * Resolution order:
 *   1. JWT access token claim (VITE_TENANT_CLAIM_NAME) — production path post-E1
 *   2. VITE_CUSTOMER_ID — dev/pre-E1 fallback (Vite dev server only)
 *
 * @throws if tenant ID cannot be resolved in production
 */
export function getTenantIdOrThrow(token: string | null): string {
  if (token) {
    const fromToken = resolveTenantIdFromToken(token);
    if (fromToken) return fromToken;
  }

  // Pre-E1 fallback: VITE_CUSTOMER_ID is set at build time.
  // In production this is 'demo' until E1 JWT claim is configured.
  // After E1, the token claim takes precedence (checked above).
  const fallback = import.meta.env.VITE_CUSTOMER_ID as string | undefined;
  if (fallback) return fallback;

  const claimName = import.meta.env.VITE_TENANT_CLAIM_NAME as string | undefined;
  const hint = claimName
    ? `Claim '${claimName}' was not found in the access token. Check that the user has been provisioned with a tenantId extension attribute (see E1 runbook in DEVELOPER_GUIDE.md).`
    : `VITE_TENANT_CLAIM_NAME is not set and VITE_CUSTOMER_ID is not configured. Set one of these to resolve the tenant.`;

  throw new Error(`Tenant ID could not be resolved from the access token. ${hint}`);
}
